import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faPipe, faXmark } from '@fortawesome/pro-solid-svg-icons';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { MultilineTextWithEllipsis } from '../TextWithEllipsis/MultilineTextWithEllipsis';
import { useDropdown } from '../../../tools/hooks';
import { SearchMenu } from '../SearchMenu/SearchMenu';
import { IValue } from '../../../entities/IPickers';
import { EContextList, EReferentials } from '../../../entities/IGlobal';
import { ContextMenu, ISingleLink } from '../ContextMenu/ContextMenu';
import { StatusMenu } from '../StatusMenu/StatusMenu';
import { DefaultMenu } from './DefauleView';
import IntlMessage from '../IntlMessage';

const Wrapper = styled.div<{ theme?: 'default' | 'filter' }>`
    cursor: pointer;
    position: relative;
    ${props => props.theme === 'filter' && css`
        color: ${colorStack.content};
        border-radius: 8px;
        background: ${colorStack.white};
        display: inline-flex;
        align-items: center;
        border: 2px solid ${colorStack.ligthGrey};
        transition: all .3s;
    `}
`;

const SelectedBox = styled.div<{ theme?: 'default' | 'filter' }>`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 8px;
    ${props => props.theme === 'filter' && css`
        padding: 10px 12px;
    `}
`;

const Selected = styled.span<{ textColor: string, bold?: boolean}>`
    ${fontSizeAndHeight[16]};
    color: ${props => props.textColor};
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    ${props => props.bold && css`
        font-weight: 600;
    `}
    span {
        ${fontSizeAndHeight[16]};
    }
`;

const ListBox = styled.div<{ alignRight?: boolean, openDropdownToTop: boolean, biggerPadding?: boolean }>`
    > div {
        left: -.5rem;
        width: auto;
        ${props => props.openDropdownToTop ? css`
            bottom: 45px;
        ` : css`
            top: ${props.biggerPadding ? '40px' : '30px'};
        `}
        ${props => props.alignRight && css`
            top: ${props.biggerPadding ? '43x' : '33px'};
            left: calc(-200% + 28px);
        `}
    }
`;

const RightIcons = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const RightIconsClearButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-right: 0.5rem;
`;

export enum EDropDownType {
    SEARCH = 'SEARCH',
    MENU = 'MENU',
    STATUS = 'STATUS',
    DEFAULT = 'DEFAULT'
}

export interface IDropDownProps {
    type: EDropDownType,
    loading?: boolean;
    options?: IValue[];
    refsOptions?: {
        context: EContextList;
        type: EReferentials;
    };
    disabled?: boolean;
    links?: ISingleLink[];
    value: IValue[];
    showClearButton?: boolean;
    alignRight?: boolean;
    triggerOpenDropdown?: boolean;
    closeDropdown?: boolean;
    openDropdownToTop?: boolean;
    className?: string;
    theme?: 'default' | 'filter';
    placeholder?: React.ReactNode;
    resetTrigger?: () => void;
    onChange?(item: IValue);
}

export const DropDown = (props: IDropDownProps) => {
    const [wrapperRef, dropdownOpen, toggleDropdown, closeDropdown] = useDropdown<HTMLDivElement>();

    useEffect(() => {
        if (props.closeDropdown) {
            closeDropdown();
        }
    }, [props.closeDropdown]);

    useEffect(() => {
        if (props.triggerOpenDropdown) {
            closeDropdown();
            setTimeout(() => {
                toggleDropdown();
                props.resetTrigger?.();
            }, 100);
        }

    }, [props.triggerOpenDropdown]);

    const changeHandler = (item: IValue) => {
        setTimeout(() => {
            toggleDropdown();
            props.resetTrigger?.();
            props.onChange(item);
        }, 100);
    };

    const clearSelection = (e: React.MouseEvent<SVGSVGElement>) => {
        props.onChange(undefined);
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Wrapper data-lc="js-lc-drop-down" ref={wrapperRef} className={props.className} theme={props.theme}>
            <SelectedBox onClick={props.disabled ? undefined : toggleDropdown} theme={props.theme}>
                <Selected textColor={props.value?.[0]?.data?.color || colorStack.content} bold={props.theme === 'filter'}>
                    <MultilineTextWithEllipsis numberOfLines={1} noTooltip>
                        {props.value?.[0]?.text ? (
                            <span>{props.value?.[0]?.text}</span>
                        ) : props.placeholder ? (
                            props.placeholder
                        ) : (
                            <IntlMessage id={props.value?.[0] ? (props.value?.[0]?.text || 'global.missingTranslation') : 'global.select'} />
                        )}
                    </MultilineTextWithEllipsis>
                </Selected>
                <RightIcons>
                    {props.showClearButton && props.value?.[0]?.text?.length > 0 && (
                        <RightIconsClearButtonContainer>
                            <FontAwesomeIcon icon={faXmark} color={colorStack.darkGrey} onClick={clearSelection} />
                            <FontAwesomeIcon icon={faPipe} color={colorStack.ligthGrey} />
                        </RightIconsClearButtonContainer>
                    )}
                    <FontAwesomeIcon icon={dropdownOpen ? faCaretUp : faCaretDown} color={colorStack.darkGrey} />
                </RightIcons>
            </SelectedBox>
            {dropdownOpen &&
                <ListBox alignRight={props.alignRight} openDropdownToTop={props.openDropdownToTop} biggerPadding={props.theme === 'filter'}>
                    {props.type === EDropDownType.SEARCH &&
                        <SearchMenu
                            loading={props.loading}
                            options={props.options}
                            refsOptions={props.refsOptions}
                            value={props.value}
                            onChange={changeHandler}
                        />
                    }
                    {(props.type === EDropDownType.MENU && props.links) &&
                        <ContextMenu
                            links={props.links}
                        />
                    }
                    {props.type === EDropDownType.STATUS &&
                        <StatusMenu
                            options={props.options}
                            value={props.value}
                            onChange={changeHandler}
                        />
                    }
                    {props.type === EDropDownType.DEFAULT &&
                        <DefaultMenu
                            options={props.options}
                            value={props.value}
                            onChange={changeHandler}
                        />
                    }
                </ListBox>
            }
        </Wrapper>
    );
};
